import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { OnboardingBanner } from '../components/kyc/OnboardingBanner';
import {
  Wallet,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  IndianRupee,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const STATUS_COLORS = {
  paid: '#22c55e',
  pending_admin_review: '#eab308',
  approved: '#3b82f6',
  processing: '#a855f7',
  failed: '#ef4444',
  rejected: '#f43f5e',
  cancelled: '#94a3b8',
  reversed: '#f97316',
  created: '#64748b',
};

const STATUS_LABELS = {
  paid: 'Paid',
  pending_admin_review: 'Pending Review',
  approved: 'Approved',
  processing: 'Processing',
  failed: 'Failed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  reversed: 'Reversed',
  created: 'Created',
};

export default function MerchantDashboard() {
  const { merchant, api } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentPayouts, setRecentPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, payoutsRes] = await Promise.all([
          api.get(`/analytics/merchant/overview?days=${period}`),
          api.get('/payouts/?limit=5'),
        ]);
        setAnalytics(analyticsRes.data);
        setRecentPayouts(payoutsRes.data.payouts || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (merchant) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [merchant, api, period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (!merchant) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="p-4 rounded-sm bg-primary/10 border border-primary/20 mb-4">
          <AlertCircle className="h-12 w-12 text-primary" />
        </div>
        <h2 className="text-2xl font-heading font-bold mb-2">Complete Your Onboarding</h2>
        <p className="text-muted-foreground mb-6 max-w-md">
          To start sending payouts, please complete your merchant onboarding process including KYC verification.
        </p>
        <Button
          onClick={() => navigate('/onboarding')}
          className="glow-primary rounded-sm"
          data-testid="start-onboarding-btn"
        >
          Start Onboarding
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="card-grid">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[320px] lg:col-span-2 rounded-lg" />
          <Skeleton className="h-[320px] rounded-lg" />
        </div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const deltas = analytics?.deltas || {};
  const volumeTrend = analytics?.volume_trend || [];
  const balanceTrend = analytics?.balance_trend || [];
  const statusBreakdown = analytics?.status_breakdown || {};

  const pieData = Object.entries(statusBreakdown).map(([status, count]) => ({
    name: STATUS_LABELS[status] || status,
    value: count,
    status,
  }));

  const renderDelta = (value, suffix) => {
    if (value === null || value === undefined) {
      return <span className="text-[11px] text-muted-foreground">new</span>;
    }
    const up = value >= 0;
    return (
      <span className={`flex items-center text-[11px] font-medium ${up ? 'text-green-500' : 'text-red-500'}`}>
        {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
        {up ? '+' : ''}
        {value}
        {suffix}
      </span>
    );
  };

  const statCards = [
    {
      title: 'Available Balance',
      value: formatCurrency(summary.available_balance),
      subtitle: 'Ready for payouts',
      icon: Wallet,
      accent: 'text-primary',
    },
    {
      title: 'Total Paid Out',
      value: formatCurrency(summary.total_paid_amount),
      subtitle: `${summary.total_payouts || 0} payouts`,
      icon: IndianRupee,
      accent: 'text-green-500',
      delta: renderDelta(deltas.paid_amount_pct, `% vs prev ${period}d`),
    },
    {
      title: 'Success Rate',
      value: `${summary.success_rate || 0}%`,
      subtitle: `${analytics?.success_metrics?.paid || 0} paid / ${analytics?.success_metrics?.failed || 0} failed`,
      icon: TrendingUp,
      accent: 'text-blue-500',
      delta: renderDelta(deltas.success_rate_points, `pts vs prev ${period}d`),
    },
    {
      title: 'Pending Payouts',
      value: summary.pending_count || 0,
      subtitle: 'Awaiting completion',
      icon: Clock,
      accent: 'text-yellow-500',
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  return (
    <div className="space-y-6 animate-stagger">
      <OnboardingBanner />

      {/* Welcome + service status */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Welcome back, {merchant.business_name || 'Merchant'}</h1>
          <p className="text-muted-foreground text-sm mt-1">Here's your payment and payout activity at a glance.</p>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-muted-foreground">MID: <span className="font-mono text-primary font-semibold" data-testid="merchant-mid">{merchant.mid || '—'}</span></span>
            <span className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${(merchant.kyc_status === 'approved' && merchant.services?.payout_enabled) ? 'bg-green-500' : 'bg-amber-500'}`} />
              Payout {(merchant.kyc_status === 'approved' && merchant.services?.payout_enabled) ? 'Active' : 'Locked'}
            </span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />Payin Inactive</span>
          </div>
        </div>
        <Button onClick={() => navigate('/dashboard/payouts/create')} data-testid="dashboard-create-payout-btn">
          <Send className="mr-2 h-4 w-4" />Create Payout
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-actions">
        {[
          { label: 'Recharge Wallet', desc: 'Add funds via USDT', icon: Wallet, to: '/dashboard/wallet/recharge', accent: 'text-primary' },
          { label: 'Create Payout', desc: 'Send to a beneficiary', icon: Send, to: '/dashboard/payouts/create', accent: 'text-green-500' },
          { label: 'Add Beneficiary', desc: 'Add a bank account', icon: CheckCircle2, to: '/dashboard/beneficiaries', accent: 'text-blue-500' },
          { label: 'View Transactions', desc: 'Track all activity', icon: TrendingUp, to: '/dashboard/transactions', accent: 'text-purple-500' },
        ].map((qa) => (
          <button key={qa.label} onClick={() => navigate(qa.to)}
            className="text-left p-4 rounded-lg border bg-card hover:border-primary/40 hover:shadow-md transition-all group"
            data-testid={`qa-${qa.label.toLowerCase().replace(/ /g, '-')}`}>
            <qa.icon className={`h-5 w-5 mb-2 ${qa.accent} group-hover:scale-110 transition-transform`} />
            <p className="font-medium text-sm">{qa.label}</p>
            <p className="text-xs text-muted-foreground">{qa.desc}</p>
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="card-grid" data-testid={`stat-card-${index}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                {stat.title}
              </CardTitle>
              <stat.icon className={`h-4 w-4 ${stat.accent}`} strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold font-mono tabular-nums ${stat.accent}`}>{stat.value}</div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                {stat.delta}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-grid lg:col-span-2" data-testid="volume-trend-chart">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Payout Volume</CardTitle>
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
              {[7, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setPeriod(d)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    period === d
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`period-toggle-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={volumeTrend}>
                  <defs>
                    <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(211, 100%, 50%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(211, 100%, 50%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickFormatter={(d) => (d ? d.slice(5) : '')}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '4px',
                    }}
                    formatter={(value) => [formatCurrency(value), 'Volume']}
                  />
                  <Area
                    type="monotone"
                    dataKey="volume"
                    stroke="hsl(211, 100%, 50%)"
                    fillOpacity={1}
                    fill="url(#volumeGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-grid" data-testid="status-breakdown-chart">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Send className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No payouts yet</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#64748b'} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '4px',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="card-grid lg:col-span-2" data-testid="balance-trend-chart">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Wallet Balance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {balanceTrend.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Wallet className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No wallet activity yet</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                  <LineChart data={balanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '4px',
                      }}
                      formatter={(value) => [formatCurrency(value), 'Balance']}
                    />
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-grid">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Recent Payouts</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => navigate('/dashboard/payouts')}
              data-testid="view-all-payouts-btn"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPayouts.length > 0 ? (
                recentPayouts.map((payout) => (
                  <div
                    key={payout.id}
                    className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(payout.status)}
                      <div>
                        <p className="text-sm font-medium font-mono">{payout.order_id}</p>
                        <p className="text-xs text-muted-foreground">{payout.beneficiary_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono tabular-nums font-medium">{formatCurrency(payout.amount)}</p>
                      <Badge variant="secondary" className="text-[10px] capitalize">
                        {STATUS_LABELS[payout.status] || payout.status}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Send className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No payouts yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
